import { Menu } from '@headlessui/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icon, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import EventCard from 'components/ComponentV2/EventCard';
import ProfilePics from 'components/candidate/ProfilePics';
import { Button } from 'components/form';
import { Row } from 'components/layout';
import { TabsType } from 'hooks/useCampaigns.hook';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useCompanyProfile } from 'hooks/UseCompanyProfile.hook';

import { useAuth } from 'lib/providers/AuthProvider';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { EventTouchpointable } from 'lib/models/event';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import CompanyEventsTable from '../CompanyEventsTable';
import styles from './CompanyEventsListing.module.scss';

export const CompanyEventsListing: React.FC = memo(() => {
  const router = useRouter();
  const { isRecruiter } = useAuth();

  const { company, companyDashboard, mutateCompanyDashboard, similarEvents } =
    useCompanyProfile();

  const scrollTabRef = useRef<HTMLDivElement | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const { setSelectedEventState } = useCampaigns();

  useEffect(() => {
    if (!isRecruiter) {
      router.replace(PAGE_ROUTES.SIGN_IN_RECRUITER);
    }
  }, [isRecruiter]);

  const {
    event_registrations = 0,
    total_count_map,
    live_count_map,
    closed_count_map,
    live_event_touchpoint_applications_avatar_url,
    live_events,
  } = companyDashboard || {};

  const { Event: live_event_count = 0 } = live_count_map || {};

  const { Event: total_event_count = 0 } = total_count_map || {};

  const { Event: closed_event_count = 0 } = closed_count_map || {};

  const event_registrations_avatar_urls = useMemo(() => {
    return live_event_touchpoint_applications_avatar_url
      ?.filter((item) => item.avatar_url !== null)
      ?.map((item) => item.avatar_url);
  }, [live_event_touchpoint_applications_avatar_url]);

  const liveEventsList = useMemo(() => {
    return live_events?.map(({ id, title }) => {
      return (
        <button
          key={id}
          onClick={() => {
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
          }}
        >
          <span>{title}</span>
        </button>
      );
    });
  }, [live_events]);

  const getViewCTA = useCallback(() => {
    return live_events ? (
      <Menu as="div" className={styles.reviewMenu}>
        <Menu.Button className={cn(styles.trigger)}>
          <Row align="center" justify="center" columnGap={8}>
            View
            <Icon iconName="icon_arrow-down" size="xsmall" />
          </Row>
        </Menu.Button>
        <Menu.Items as="ul" className={styles.touchpoint}>
          <Menu.Item as="li" className={styles.item}>
            {liveEventsList}
          </Menu.Item>
        </Menu.Items>
      </Menu>
    ) : (
      <></>
    );
  }, [live_events]);

  const onHeaderShow = useCallback(() => {
    setHeaderScrolled(false);
  }, []);

  const onHeaderHide = useCallback(() => {
    setHeaderScrolled(true);
  }, []);

  useVisibleOnIntersect({
    elRef: headerRef,
    threshold: 1,
    onIntersect: onHeaderShow,
    onHidden: onHeaderHide,
    rootMargin: '0px',
    isEqualToThreshold: true,
  });

  const similarEventList = useMemo(() => {
    return similarEvents?.map(
      ({
        id,
        title,
        slug,
        touchpointable_type,
        start_date,
        avatar_urls,
        applications,
        touchpointable: touchpointableResponse,
      }) => {
        const url = getOpportunityPublicPagePathname(touchpointable_type, slug);
        const touchpointable = parseResponse(
          touchpointableResponse as EventTouchpointable
        );

        return (
          <div key={id} className={styles.similarlistCard}>
            <EventCard
              key={id}
              title={title}
              url={url}
              start_date={start_date}
              avatar_urls={avatar_urls}
              applications={applications}
              icon_url={touchpointable.icon_url}
              className={styles.eventcard}
              onNewTab={true}
              registrationsDesc={' students already registered to this event.'}
              titleClassname={styles.eventcardTitle}
              dateClassname={styles.eventcardDate}
              application_deadline={''}
              cities={null}
              cohost_applications={null}
              showDetails={false}
            />
          </div>
        );
      }
    );
  }, [similarEvents]);

  useEffect(() => {
    return () => {
      scrollTabRef.current = null;
    };
  }, []);

  return (
    <section
      className={styles.CompanyEventsListing}
      data-testid="CompanyEventsListing"
    >
      {headerScrolled ? null : (
        <Row
          align="center"
          columnGap={4}
          className={styles.redirect}
          onClick={() => router.push(PAGE_ROUTES.RECRUITER_DASHBOARD)}
        >
          <Icon iconName="chevron-left" size="large" />
          Dashboard
        </Row>
      )}
      <div
        ref={headerRef}
        className={cn({
          [styles.header]: true,
          [styles.headerScrolled]: headerScrolled,
        })}
      >
        <p className={styles.title}>Events</p>
        <ButtonV3
          color={total_event_count === 0 ? 'secondary' : 'primary'}
          onClick={() =>
            router.push(
              `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${TouchpointType.Event}`
            )
          }
          className={cn({ [styles.whitebutton]: total_event_count === 0 })}
        >
          Create new
        </ButtonV3>
      </div>
      {headerScrolled || !companyDashboard ? null : (
        <p className={styles.totalcount}>{total_event_count} Events</p>
      )}
      {!companyDashboard ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.container}>
          {total_event_count === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.noResultsContent}>
                <Icon iconName="desert" size="auto" />
                <div className={styles.noResultsContentText}>
                  <p>Host your first event</p>
                  <span>
                    Looking to engage with students & build a pipeline of
                    interested candidates? Host an event and promote it with the
                    top student organisations.
                  </span>
                  <br />
                  <br />
                  <span>
                    Message societies to partner with them, once the event is
                    posted you can invite them as co-hosts.
                  </span>
                </div>
                <Row columnGap={8}>
                  <ButtonV3
                    onClick={() =>
                      router.push(
                        `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${TouchpointType.Event}`
                      )
                    }
                    className={styles.noResultsContentButton}
                  >
                    Host your event
                  </ButtonV3>
                  <ButtonV3
                    color="secondary"
                    onClick={() => router.push(PAGE_ROUTES.STUDENT_SOCIETIES)}
                    className={styles.noResultsContentButton}
                    borderless
                  >
                    Message societies
                  </ButtonV3>
                </Row>
              </div>
              <div className={styles.separator}></div>
              <p className={styles.similarlistTitle}>
                Here are a few live events from societies, check them out!
                <br />
                <br />
                Get involved by messaging the society, they can add you as a
                cohost.
              </p>
              <div className={styles.similarlist}>{similarEventList}</div>
              <ButtonV3
                onClick={() =>
                  router.push(
                    `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${TouchpointType.Event}`
                  )
                }
                className={styles.similarlistButton}
                isFullWidth
              >
                Create new
              </ButtonV3>
            </div>
          ) : (
            <>
              <section className={styles.island1}>
                {event_registrations > 0 ? (
                  <div
                    className={cn(styles.island1Div, styles.island1DivActive)}
                  >
                    <div>
                      <p className={styles.island1Title}>Registrations</p>
                      <p className={styles.island1Count}>
                        {event_registrations}
                      </p>
                    </div>
                    {event_registrations > 0 ? (
                      <div>
                        {event_registrations > 2 ? (
                          <Row
                            columnGap={12}
                            className={cn(styles.subscriberRow)}
                          >
                            <ProfilePics
                              maxPicsToShow={3}
                              urls={event_registrations_avatar_urls || []}
                            />
                          </Row>
                        ) : null}
                        {getViewCTA()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>Live Events</p>
                    <p className={styles.island1Count}>{live_event_count}</p>
                  </div>
                  <Row columnGap={10} isFullWidthRow>
                    <Button
                      color="white"
                      className={styles.whitebutton}
                      onClick={() => {
                        setSelectedEventState(TabsType.LIVE);
                        scrollTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                      disabled={live_event_count === 0}
                    >
                      View
                    </Button>
                    <Button
                      color="white"
                      className={styles.whitebutton}
                      onClick={() =>
                        router.push(
                          `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${TouchpointType.Event}`
                        )
                      }
                    >
                      Add new
                    </Button>
                  </Row>
                </div>
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>Ended events</p>
                    <p className={styles.island1Count}>{closed_event_count}</p>
                  </div>
                  {closed_event_count ? (
                    <Button
                      color="white"
                      className={styles.whitebutton}
                      onClick={() => {
                        setSelectedEventState(TabsType.ENDED);
                        scrollTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                      disabled={closed_event_count === 0}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
              </section>

              <div ref={scrollTabRef}>
                {company && (
                  <CompanyEventsTable
                    mutateCompanyDashboard={mutateCompanyDashboard}
                    companyId={company.id}
                    companySlug={company.slug}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
});

CompanyEventsListing.displayName = 'CompanyEventsListing';
